import { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db, isDemoMode } from "../firebase";
import { DEMO_DISCOVERY_EDGES, DEMO_PROFILES, DEMO_USER } from "../demoData";
import {
  buildDiscoveryGraph,
  createSnapshotDiscoveryAdapter,
  discoveryErrorMessage,
} from "../lib/discoveryGraph";
import { publishProfile } from "../lib/profilePublication";
import { loadPublishedProfile } from "../lib/profileStore";
import { knownLocationIndex, withKnownLocations } from "../lib/locationClusters";

function snapshotEdges(snapshot) {
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

function snapshotFailure(uid, error) {
  return {
    stage: "snapshot",
    uid,
    message: error instanceof Error ? error.message : String(error || "Network snapshot failed"),
  };
}

function withSnapshotFailure(graph, uid, error) {
  if (!error) return graph;
  const errors = [snapshotFailure(uid, error), ...(graph.errors || [])];
  return {
    ...graph,
    errors,
    partial: true,
    error: discoveryErrorMessage(errors),
  };
}

/** Build the authenticated, privacy-safe introduction graph from network_edges. */
export function useDiscoveryGraph(currentUid, ownEntries = [], options = {}) {
  const { projectProfile = publishProfile } = options;
  const [state, setState] = useState({
    nodes: [],
    edges: [],
    loading: false,
    error: null,
    truncated: false,
    partial: false,
    errors: [],
  });
  const [topology, setTopology] = useState({
    uid: null,
    edges: [],
    ready: isDemoMode,
    error: null,
  });

  useEffect(() => {
    if (isDemoMode) return undefined;
    if (!db || !currentUid) {
      setTopology({ uid: currentUid || null, edges: [], ready: false, error: null });
      return undefined;
    }

    setTopology({ uid: currentUid, edges: [], ready: false, error: null });
    return onSnapshot(
      collection(db, "network_edges"),
      (snapshot) => {
        setTopology({
          uid: currentUid,
          edges: snapshotEdges(snapshot),
          ready: true,
          error: null,
        });
      },
      (error) => {
        // Retain the last usable topology, but make its staleness explicit.
        setTopology((previous) => ({
          uid: currentUid,
          edges: previous.uid === currentUid ? previous.edges : [],
          ready: true,
          error,
        }));
      }
    );
  }, [currentUid]);

  useEffect(() => {
    if (!currentUid) {
      setState({
        nodes: [], edges: [], loading: false, error: null, truncated: false, partial: false, errors: [],
      });
      return undefined;
    }
    const topologyReady = isDemoMode || (topology.uid === currentUid && topology.ready);
    if (!topologyReady) {
      setState((previous) => ({
        ...previous,
        nodes: previous.nodes[0]?.id === currentUid ? previous.nodes : [],
        edges: previous.nodes[0]?.id === currentUid ? previous.edges : [],
        loading: true,
        error: null,
        partial: false,
        errors: [],
      }));
      return undefined;
    }

    let cancelled = false;
    const run = async () => {
      setState((previous) => ({ ...previous, loading: true, error: null }));
      try {
        const adapter = isDemoMode
          ? createSnapshotDiscoveryAdapter({
              edges: DEMO_DISCOVERY_EDGES,
              profiles: { ...DEMO_PROFILES, [currentUid]: DEMO_USER },
            })
          : createSnapshotDiscoveryAdapter({
              edges: topology.edges,
              loadProfile: loadPublishedProfile,
            });
        const builtGraph = await buildDiscoveryGraph({
          currentUid,
          ownEntries,
          adapter,
          projectProfile,
        });
        const graph = withSnapshotFailure(
          {
            ...builtGraph,
            // Seat people using the owner's own record of where they live, so
            // this map and the dashboard never disagree about one person.
            nodes: withKnownLocations(builtGraph.nodes, knownLocationIndex(ownEntries)),
          },
          currentUid,
          isDemoMode ? null : topology.error
        );
        if (!cancelled) setState({ ...graph, loading: false });
      } catch (error) {
        if (!cancelled) {
          setState((previous) => ({
            ...previous,
            loading: false,
            partial: true,
            error: error instanceof Error ? error.message : String(error),
          }));
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [currentUid, ownEntries, projectProfile, topology]);

  return state;
}
